/**
 * Inline display-name editor for /me.
 *
 * Two visible states: a read row with an "Edit" button, and an edit
 * form (TextInput + Save / Cancel + "Reset to Entra default" when the
 * value is currently overridden).
 *
 * Submitting calls `PATCH /api/v1/me`. The backend flips
 * `display_name_user_overridden=True` so a future Entra-side rename does
 * NOT silently revert the user's choice (see docs/architecture/user-management.md
 * §6.2 + `test_jit_sync_respects_display_name_override`).
 *
 * UI-only — every authorisation decision is the backend's. We surface
 * the override flag to the user so they understand which value will
 * survive the next sign-in.
 */

import { useState } from "react";
import { ActionIcon, Badge, Button, Group, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { ApiError } from "@/shared/api/apiFetch";
import { type Me, patchMe } from "@/shared/api/me";

interface Props {
  me: Me;
  onUpdated: (next: Me) => void;
}

export function EditDisplayName({ me, onUpdated }: Props) {
  const getToken = useAccessToken();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(me.display_name);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setValue(me.display_name);
    setError(null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setError(null);
  }

  async function save() {
    if (value.trim().length === 0) {
      setError("Display name cannot be empty.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const updated = await patchMe(token, { display_name: value });
      onUpdated(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function clearOverride() {
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const updated = await patchMe(token, { display_name: null });
      onUpdated(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!editing) {
    return (
      <Group gap="sm" align="center" wrap="nowrap">
        <Text size="sm" fw={500}>
          {me.display_name}
        </Text>
        {me.display_name_user_overridden && (
          <Tooltip label="You've overridden the directory-sourced display name. It will not be replaced on next sign-in.">
            <Badge color="blue" variant="light" size="sm">
              Custom
            </Badge>
          </Tooltip>
        )}
        <Tooltip label="Edit display name">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={startEdit}
            aria-label="Edit display name"
          >
            <IconPencil size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    );
  }

  return (
    <Stack gap="xs">
      <Group gap="xs" align="end" wrap="wrap">
        <TextInput
          label="Display name"
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
          disabled={submitting}
          autoFocus
          maxLength={200}
          style={{ flex: "1 1 240px" }}
          aria-label="Display name"
        />
        <Button onClick={save} loading={submitting} disabled={value.trim().length === 0}>
          Save
        </Button>
        <Button variant="subtle" onClick={cancel} disabled={submitting}>
          Cancel
        </Button>
        {me.display_name_user_overridden && (
          <Button
            variant="subtle"
            color="red"
            onClick={clearOverride}
            disabled={submitting}
            aria-label="Reset to Entra default"
          >
            Reset to directory default
          </Button>
        )}
      </Group>
      {error && (
        <Text size="sm" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
}
